import { getConversionsMetrics } from '@/views/reports/pickers/base/conversions/columns';
import { POSTBACKS_METRICS } from '@/views/reports/pickers/base/postbacks/columns';
import { ERRORS_METRICS } from '@/views/reports/pickers/base/errors/columns';
import { LIVE_VISITS_METRICS } from '@/views/reports/pickers/base/live-visits/columns';
import { LIVE_CLICKS_METRICS } from '@/views/reports/pickers/base/live-clicks/columns';
import { COMMON_METRICS } from '@/views/reports/pickers/base/columns';
import {
  METRICS_CUSTOM_COLUMN,
  METRICS_CUSTOM_EVENT,
  METRICS_CUSTOM_EVENT_HIDDEN_REVENUE,
  METRICS_CUSTOM_EVENT_REVENUE,
} from '@/services/constants/report';

const getDefaultColumnWeight = ({ key, mainColumn }: { key: string; mainColumn?: string }) => {
  return mainColumn === key ? 300 : 100;
};

const defaultColumnsConfig = ({ allColumns, defaultSelected, defaultUnselected, order, weight }) => {
  const columns = allColumns.filter(
    (value) => defaultSelected.includes(value) || !defaultUnselected.includes(value),
  );

  const width = columns.reduce((accumulator, column) => {
    accumulator[column] = weight[column] || getDefaultColumnWeight({ key: column });
    return accumulator;
  }, {});

  return {
    columns,
    order,
    width,
  };
};

const conversionsColumns = () => {
  return {
    context: 'conversions',
    ...defaultColumnsConfig(getConversionsMetrics()),
  };
};

const postbacksColumns = () => {
  return {
    context: 'postbacks',
    ...defaultColumnsConfig(POSTBACKS_METRICS),
  };
};

const errorsColumns = () => {
  return {
    context: 'errors',
    ...defaultColumnsConfig(ERRORS_METRICS),
  };
};

const getDefaultColumnsMetric = (dimensions: Record<string, unknown>, metrics: unknown[]) => {
  const { defaultSelected, defaultUnselected } = COMMON_METRICS;

  return Object.entries(dimensions).reduce((accumulator, [key, value]: [string, any]) => {
    const columns = [value.mainColumn, ...value.additionalColumns, ...metrics].filter((column) => {
      if (
        column.startsWith(METRICS_CUSTOM_COLUMN) ||
        column.startsWith(METRICS_CUSTOM_EVENT) ||
        column.startsWith(METRICS_CUSTOM_EVENT_REVENUE) ||
        column.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)
      ) {
        return false;
      }

      return defaultSelected.includes(column) || !defaultUnselected.includes(column);
    });

    accumulator[key] = {
      columns,
      context: key,
      width: columns.reduce((widthAccumulator, column) => {
        widthAccumulator[column] = getDefaultColumnWeight({ key: column, mainColumn: value.mainColumn });
        return widthAccumulator;
      }, {}),
      order: {
        orderBy: COMMON_METRICS.defaultSort.find((item) => columns.includes(item)),
        orderDir: 'desc',
      },
    };
    return accumulator;
  }, {});
};

export const buildDefaultColumns = (
  dimensions: Record<string, unknown>,
  metrics: unknown[],
): ColumnsSettingsContextServerFormat => {
  return {
    ...getDefaultColumnsMetric(dimensions, metrics),
    conversions: conversionsColumns(),
    postbacks: postbacksColumns(),
    errors: errorsColumns(),
    'live-clicks': {
      context: 'live-clicks',
      ...defaultColumnsConfig(LIVE_CLICKS_METRICS),
    },
    'live-visits': {
      context: 'live-visits',
      ...defaultColumnsConfig(LIVE_VISITS_METRICS),
    },
  } as ColumnsSettingsContextServerFormat;
};
